import { useState, useEffect, useCallback, useRef } from 'react'
import consumer from '@/channels/consumer'

export interface AppNotification {
  id: number
  notification_type: string
  title: string
  body: string | null
  url: string | null
  resource_type: string | null
  resource_id: number | null
  read: boolean
  read_at: string | null
  created_at: string
}

interface NotificationState {
  notifications: AppNotification[]
  unreadCount: number
  loading: boolean
}

export function useNotificationCenter() {
  const [state, setState] = useState<NotificationState>({
    notifications: [],
    unreadCount: 0,
    loading: true,
  })

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/notifications?limit=30', {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
      })
      if (!response.ok) return
      const data = await response.json()
      setState({
        notifications: data.notifications,
        unreadCount: data.unread_count,
        loading: false,
      })
    } catch {
      setState(prev => ({ ...prev, loading: false }))
    }
  }, [])

  // Initial fetch
  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  // Subscribe to real-time notification updates
  useEffect(() => {
    const subscription = consumer.subscriptions.create(
      { channel: 'AgentNotificationsChannel' },
      {
        received(message: any) {
          if (message.type === 'notification') {
            setState(prev => ({
              notifications: [message.notification, ...prev.notifications].slice(0, 50),
              unreadCount: prev.unreadCount + 1,
              loading: false,
            }))
          }
        },
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const markAllRead = useCallback(async () => {
    try {
      const csrfMeta = document.querySelector('meta[name="csrf-token"]')
      const csrfToken = csrfMeta?.getAttribute('content') || ''
      const response = await fetch('/api/v1/notifications/read_all', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'same-origin',
      })
      if (!response.ok) return
      setState(prev => ({
        ...prev,
        notifications: prev.notifications.map(n => ({ ...n, read: true, read_at: new Date().toISOString() })),
        unreadCount: 0,
      }))
    } catch {
      // silently ignore
    }
  }, [])

  const markRead = useCallback(async (id: number) => {
    try {
      const csrfMeta = document.querySelector('meta[name="csrf-token"]')
      const csrfToken = csrfMeta?.getAttribute('content') || ''
      await fetch(`/api/v1/notifications/${id}/read`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'same-origin',
      })
      setState(prev => ({
        ...prev,
        notifications: prev.notifications.map(n =>
          n.id === id ? { ...n, read: true, read_at: new Date().toISOString() } : n
        ),
        unreadCount: Math.max(0, prev.unreadCount - 1),
      }))
    } catch {
      // silently ignore
    }
  }, [])

  const dismissNotification = useCallback(async (id: number) => {
    try {
      const csrfMeta = document.querySelector('meta[name="csrf-token"]')
      const csrfToken = csrfMeta?.getAttribute('content') || ''
      await fetch(`/api/v1/notifications/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'same-origin',
      })
      setState(prev => {
        const removed = prev.notifications.find(n => n.id === id)
        return {
          ...prev,
          notifications: prev.notifications.filter(n => n.id !== id),
          unreadCount: removed && !removed.read ? Math.max(0, prev.unreadCount - 1) : prev.unreadCount,
        }
      })
    } catch {
      // silently ignore
    }
  }, [])

  return {
    ...state,
    markAllRead,
    markRead,
    dismissNotification,
    refresh: fetchNotifications,
  }
}
